import { createSlice } from '@reduxjs/toolkit';

import { getCalendarURL } from '../api/commonHTTP';
import { DEFAULT_PAGE_LOAD_SIZE } from '../api/pagination';

import { apiCallBegan, apiVersion } from './api';

const slice = createSlice({
    name: 'calendarService',
    initialState: {
        appointmentAuditData: [],
        appointmentAttendanceDetails: [],
        appointmentAttendanceDetailsMetaData: {},
        mostRecentAppointment: {},
        participantsLastAndNextAppointments: {},

        appointmentSearch: [],
        appointmentSearchMetaData: {},
        appointmentSlotsSearch: [],
        appointmentSlotsSearchMetaData: {},
        appointmentsAndSlotsSearch: [],
        appointmentsAndSlotsSearchMetaData: {},
        appointmentsAndSlotsByUser: {},
        appointmentsAndSlotsByUserMetaData: {},
        appointmentsAndSlotsByUserAndDate: {},
        appointmentsAndSlotsByUserAndDateMetaData: {},
        appointmentsAndSlotsByUserAndDateRange: {},
        appointmentsAndSlotsByUserAndDateRangeMetaData: {},
        appointmentsAndSlotsByParticipantAndDateRange: {},
        appointmentsAndSlotsByParticipantAndDateRangeMetaData: {},

        baseAppointment: {},
        newAppointment: {},
        usersCurrentAppointment: {},
        participantsCurrentAppointment: {},
        appointmentsCreated: {},

        userEventsCreated: {},
        usersCurrentEvent: {},

        userEventsByUserAndDate: {},
        userEventsByUserAndDateMetaData: {},
        userEventsByUserAndDateRange: {},
        userEventsByUserAndDateRangeMetaData: {},

        loadedAppointmentUpdate: false,
        loadingAppointmentAuditData: false,
        loadingAppointmentAttendanceDetails: false,
        loadingAppointmentSearch: false,
        loadingAppointmentSlotsSearch: false,
        loadingAppointmentsAndSlotsSearch: false,
        loadingBaseAppointment: false,
        loadingUsersAppointmentsAndSlots: false,
        loadingAppointmentsAndSlotsByUserAndDate: false,
        loadingAppointmentsAndSlotsByParticipantAndDateRange: false,
        loadingUserEventsByUserAndDate: false,
        loadingUserEventsByUserAndDateRange: false,

        openAppointmentSchedulerPanel: false,
        userDataKeys: {},
        participantDataKeys: {},
        successMessage: '',
        errorMessage: '',
        version: {}
    },
    reducers: {
        // audit
        appointmentAuditDataLoading: (state) => {
            state.loadingAppointmentAuditData = true;
            state.appointmentAuditData = [];
        },
        appointmentAuditDataLoaded: (state, action) => {
            state.loadingAppointmentAuditData = false;
            state.appointmentAuditData = action.payload;
        },
        appointmentAuditDataLoadingFailure: (state, action) => {
            state.loadingAppointmentAuditData = false;
            state.appointmentAuditData = [];
            state.errorMessage = action.payload;
        },
        clearAppointmentAuditData: (state) => {
            state.appointmentAuditData = [];
            state.loadingAppointmentAuditData = false;
        },

        // attendance and related
        appointmentAttendanceDetailsSearchLoading: (state) => {
            state.loadingAppointmentAttendanceDetails = true;
        },
        appointmentAttendanceDetailsSearchLoaded: (state, action) => {
            state.loadingAppointmentAttendanceDetails = false;
            if ('content' in action.payload) {
                const { content, ...rest } = action.payload;
                state.appointmentAttendanceDetails = rest.first
                    ? content
                    : [...state.appointmentAttendanceDetails, ...content];
                state.appointmentAttendanceDetailsMetaData = rest;
            } else {
                state.appointmentAttendanceDetails = action.payload;
            }
        },
        appointmentAttendanceDetailsSearchLoadingFailure: (state, action) => {
            state.loadingAppointmentAttendanceDetails = false;
            state.errorMessage = action.payload;
        },

        appointmentAttendanceDetailsLoading: (state) => {
            state.loadingAppointmentAttendanceDetails = true;
            state.appointmentAttendanceDetails = [];
        },
        appointmentAttendanceDetailsLoaded: (state, action) => {
            state.loadingAppointmentAttendanceDetails = false;
            state.appointmentAttendanceDetails = action.payload;
        },
        appointmentAttendanceDetailsLoadingFailure: (state, action) => {
            state.loadingAppointmentAttendanceDetails = false;
            state.errorMessage = action.payload;
        },
        clearAppointmentAttendanceDetails: (state) => {
            state.appointmentAttendanceDetails = [];
            state.appointmentAttendanceDetailsMetaData = {};
        },

        mostRecentAppointmentAttendanceDetailsLoaded: (state, action) => {
            state.mostRecentAppointment = action.payload;
        },

        appointmentAttendanceDetailUpdated: (state, action) => {
            state.appointmentAttendanceDetails = state.appointmentAttendanceDetails.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        participantsLastAndNextAppointmentsLoaded: (state, action) => {
            state.participantsLastAndNextAppointments = action.payload?.participants || {};
        },
        clearParticipantsLastAndNextAppointments: (state) => {
            state.participantsLastAndNextAppointments = {};
        },

        // appointment search
        appointmentSearchLoading: (state) => {
            state.loadingAppointmentSearch = true;
        },
        appointmentSearchLoaded: (state, action) => {
            state.loadingAppointmentSearch = false;
            const { content, ...rest } = action.payload;
            state.appointmentSearch = rest.first
                ? content
                : [...state.appointmentSearch, ...content];
            state.appointmentSearchMetaData = rest;
        },
        appointmentSearchLoadingFailure: (state, action) => {
            state.loadingAppointmentSearch = false;
            state.errorMessage = action.payload;
        },
        clearAppointmentSearch: (state) => {
            state.appointmentSearch = [];
            state.appointmentSearchMetaData = {};
        },

        // appointment slot search
        appointmentSlotsSearchLoading: (state) => {
            state.loadingAppointmentSlotsSearch = true;
        },
        appointmentSlotsSearchLoaded: (state, action) => {
            state.loadingAppointmentSlotsSearch = false;
            const { content, ...rest } = action.payload;
            if (Array.isArray(content))
                state.appointmentSlotsSearch = rest.first
                    ? content
                    : [...state.appointmentSlotsSearch, ...content];
            state.appointmentSlotsSearchMetaData = rest;
        },
        appointmentSlotsSearchLoadingFailure: (state, action) => {
            state.loadingAppointmentSlotsSearch = false;
            state.errorMessage = action.payload;
        },
        clearAppointmentSlotsSearch: (state) => {
            state.appointmentSlotsSearch = [];
            state.appointmentSlotsSearchMetaData = {};
        },

        // non-calendar components use this
        appointmentsAndSlotsSearchLoading: (state) => {
            state.loadingAppointmentsAndSlotsSearch = true;
        },
        appointmentsAndSlotsSearchLoaded: (state, action) => {
            state.loadingAppointmentsAndSlotsSearch = false;
            const { content, ...rest } = action.payload;
            state.appointmentsAndSlotsSearch = rest.first
                ? content
                : [...state.appointmentsAndSlotsSearch, ...content];
            state.appointmentsAndSlotsSearchMetaData = rest;
        },
        appointmentsAndSlotsSearchLoadingFailure: (state, action) => {
            state.loadingAppointmentsAndSlotsSearch = false;
            state.errorMessage = action.payload;
        },
        clearAppointmentsAndSlotsSearch: (state) => {
            state.appointmentsAndSlotsSearch = [];
            state.appointmentsAndSlotsSearchMetaData = {};
        },

        // create appointment / slot - non calendar.
        appointmentCreating: (state) => {
            state.newAppointment = {};
        },
        appointmentCreated: (state, action) => {
            state.newAppointment = action.payload;
        },

        appointmentLoaded: (state, action) => {
            state.usersCurrentAppointment = action.payload;
        },

        baseAppointmentLoading: (state) => {
            state.loadingBaseAppointment = true;
        },
        baseAppointmentLoaded: (state, action) => {
            state.loadingBaseAppointment = false;
            state.baseAppointment = action.payload;
        },
        baseAppointmentLoadingFailure: (state, action) => {
            state.loadingBaseAppointment = false;
            state.errorMessage = action.payload;
        },

        // scheduling assistant - by date
        usersAppointmentsAndSlotsByDateLoading: (state) => {
            state.loadingAppointmentsAndSlotsByUserAndDate = true;
        },
        usersAppointmentsAndSlotsByDateLoaded: (state, action) => {
            state.loadingAppointmentsAndSlotsByUserAndDate = false;
            const { content, ...rest } = action.payload;
            if (content?.length > 0) {
                content?.forEach((el) => {
                    if (`${el.userId}_${el.date}` in state.appointmentsAndSlotsByUserAndDate) {
                        state.appointmentsAndSlotsByUserAndDate[`${el.userId}_${el.date}`] = [
                            ...state.appointmentsAndSlotsByUserAndDate[
                                `${el.userId}_${el.date}`
                            ].filter((entry) => entry.id !== el.id),
                            el
                        ];
                    } else {
                        state.appointmentsAndSlotsByUserAndDate = {
                            ...state.appointmentsAndSlotsByUserAndDate,
                            [`${el.userId}_${el.date}`]: [el]
                        };
                    }
                });
            }

            state.appointmentsAndSlotsByUserAndDateMetaData = rest;
        },
        usersAppointmentsAndSlotsByDateLoadingFailure: (state, action) => {
            state.loadingAppointmentsAndSlotsByUserAndDate = false;
            state.errorMessage = action.payload;
        },

        // user appointments by date range
        appointmentsAndSlotsByUserAndDateRangeLoading: (state, action) => {
            state.loadingUsersAppointmentsAndSlotsByUserAndDateRange = true;
            state.userDataKeys = action.payload;
        },
        appointmentsAndSlotsByUserAndDateRangeLoaded: (state, action) => {
            state.loadingUsersAppointmentsAndSlotsByUserAndDateRange = false;
            const { userId, dateFrom, dateTo } = state.userDataKeys;
            const { content, ...rest } = action.payload;
            if (`${userId}_${dateFrom}_${dateTo}` in state.appointmentsAndSlotsByUserAndDateRange) {
                state.appointmentsAndSlotsByUserAndDateRange[`${userId}_${dateFrom}_${dateTo}`] =
                    rest.first
                        ? content
                        : [
                              ...state.appointmentsAndSlotsByUserAndDateRange[
                                  `${userId}_${dateFrom}_${dateTo}`
                              ],
                              ...content
                          ];
            } else {
                state.appointmentsAndSlotsByUserAndDateRange = {
                    ...state.appointmentsAndSlotsByUserAndDateRange,
                    [`${userId}_${dateFrom}_${dateTo}`]: content
                };
            }
            state.appointmentsAndSlotsByUserAndDateRangeMetaData = rest;
        },
        appointmentsAndSlotsByUserAndDateRangeLoadingFailure: (state, action) => {
            state.loadingUsersAppointmentsAndSlotsByUserAndDateRange = false;
            state.errorMessage = action.payload;
        },
        clearAppointmentsAndSlotsByUserAndDateRange: (state) => {
            state.appointmentsAndSlotsByUserAndDateRange = {};
            state.appointmentsAndSlotsByUserAndDateRangeMetaData = {};
            state.userDataKeys = {};
        },

        // Create appointments / slots in User calendar
        userCalendarAppointmentCreating: (state, action) => {
            state.newAppointment = {};
            state.userDataKeys = action.payload;
        },
        userCalendarAppointmentCreated: (state, action) => {
            state.newAppointment = action.payload;
            const { userId } = state.userDataKeys;
            state.appointmentsCreated = {
                ...state.appointmentsCreated,
                [userId]:
                    userId in state.appointmentsCreated
                        ? [...state.appointmentsCreated[userId], action.payload]
                        : [action.payload]
            };
            state.userDataKeys = {};
        },
        clearAppointmentsCreated: (state) => {
            state.appointmentsCreated = {};
        },

        // Update appointments / slots in User Calendar
        userCalendarAppointmentUpdating: (state, action) => {
            state.userDataKeys = action.payload;
        },
        userCalendarAppointmentUpdated: (state, action) => {
            state.usersCurrentAppointment = action.payload;
            const { userId, id } = state.userDataKeys;
            if (userId in state.appointmentsCreated) {
                state.appointmentsCreated[userId] = state.appointmentsCreated[userId].map((el) =>
                    el.id === id ? action.payload : el
                );
            }
            state.appointmentsAndSlotsByUserAndDateRange = Object.entries(
                state.appointmentsAndSlotsByUserAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.map((el) => (el.id === action.payload.id ? action.payload : el))
                }),
                {}
            );
            state.userDataKeys = {};
        },

        // Delete appointments / slots in User Calendar
        userCalendarAppointmentDeleting: (state, action) => {
            state.userDataKeys = action.payload;
        },
        userCalendarAppointmentDeleted: (state, action) => {
            const { userId } = state.userDataKeys;
            if (userId in state.appointmentsCreated) {
                state.appointmentsCreated[userId] = state.appointmentsCreated[userId].filter(
                    (el) => el.id !== action.payload.id
                );
            }
            state.appointmentsAndSlotsByUserAndDateRange = Object.entries(
                state.appointmentsAndSlotsByUserAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.filter((el) => el.id !== action.payload.id)
                }),
                {}
            );
            state.userDataKeys = {};
        },

        // PT appointments by date range
        appointmentsAndSlotsByParticipantAndDateRangeLoading: (state, action) => {
            state.loadingAppointmentsAndSlotsByParticipantAndDateRange = true;
            state.participantDataKeys = action.payload;
        },
        appointmentsAndSlotsByParticipantAndDateRangeLoaded: (state, action) => {
            state.loadingAppointmentsAndSlotsByParticipantAndDateRange = false;
            const { participantId, dateFrom, dateTo } = state.participantDataKeys;
            const { content, ...rest } = action.payload;
            if (
                `${participantId}_${dateFrom}_${dateTo}` in
                state.appointmentsAndSlotsByParticipantAndDateRange
            ) {
                state.appointmentsAndSlotsByParticipantAndDateRange[
                    `${participantId}_${dateFrom}_${dateTo}`
                ] = rest.first
                    ? content
                    : [
                          ...state.appointmentsAndSlotsByParticipantAndDateRange[
                              `${participantId}_${dateFrom}_${dateTo}`
                          ],
                          ...content
                      ];
            } else {
                state.appointmentsAndSlotsByParticipantAndDateRange = {
                    ...state.appointmentsAndSlotsByParticipantAndDateRange,
                    [`${participantId}_${dateFrom}_${dateTo}`]: content
                };
            }
            state.appointmentsAndSlotsByParticipantAndDateRangeMetaData = rest;
        },
        appointmentsAndSlotsByParticipantAndDateRangeLoadingFailure: (state, action) => {
            state.loadingAppointmentsAndSlotsByParticipantAndDateRange = false;
            state.errorMessage = action.payload;
        },
        clearAppointmentsAndSlotsByParticipantAndDateRange: (state) => {
            state.appointmentsAndSlotsByParticipantAndDateRange = {};
            state.appointmentsAndSlotsByParticipantAndDateRangeMetaData = {};
            state.participantDataKeys = {};
        },
        clearAppointmentsAndSlotsByParticipantAndDateRangeMetaData: (state) => {
            state.appointmentsAndSlotsByParticipantAndDateRangeMetaData = {};
        },

        setParticipantsCurrentAppointment: (state, action) => {
            state.participantsCurrentAppointment = action.payload;
        },

        // Create appointments / slots in PT calendar
        participantCalendarAppointmentCreating: (state, action) => {
            state.participantDataKeys = action.payload;
            state.newAppointment = {};
        },
        participantCalendarAppointmentCreated: (state, action) => {
            state.newAppointment = action.payload;
            const { participantId } = state.participantDataKeys;
            state.appointmentsCreated = {
                ...state.appointmentsCreated,
                [participantId]:
                    participantId in state.appointmentsCreated
                        ? [...state.appointmentsCreated[participantId], action.payload]
                        : [action.payload]
            };
            state.participantDataKeys = {};
        },

        // Update appointments / slots in PT Calendar
        participantCalendarAppointmentUpdating: (state, action) => {
            state.participantDataKeys = action.payload;
            state.loadedAppointmentUpdate = false;
        },
        participantCalendarAppointmentUpdated: (state, action) => {
            const { participantId, id } = state.participantDataKeys;
            if (participantId in state.appointmentsCreated) {
                state.appointmentsCreated[participantId] = state.appointmentsCreated[
                    participantId
                ].map((el) => (el.id === id ? action.payload : el));
            }
            state.participantsCurrentAppointment = action.payload;
            state.appointmentsAndSlotsByParticipantAndDateRange = Object.entries(
                state.appointmentsAndSlotsByParticipantAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.map((el) => (el.id === action.payload.id ? action.payload : el))
                }),
                {}
            );
            state.loadedAppointmentUpdate = true;
            state.participantDataKeys = {};
        },

        // Delete appointments / slots in PT Calendar
        participantCalendarAppointmentDeleting: (state, action) => {
            state.participantDataKeys = action.payload;
            state.loadedAppointmentUpdate = false;
        },
        participantCalendarAppointmentDeleted: (state, action) => {
            const { participantId } = state.participantDataKeys;
            if (participantId in state.appointmentsCreated) {
                state.appointmentsCreated[participantId] = state.appointmentsCreated[
                    participantId
                ].filter((el) => el.id !== action.payload.id);
            }
            state.appointmentsAndSlotsByParticipantAndDateRange = Object.entries(
                state.appointmentsAndSlotsByParticipantAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.filter((el) => el.id !== action.payload.id)
                }),
                {}
            );
            state.loadedAppointmentUpdate = true;
            state.participantDataKeys = {};
        },

        // Create appointments / slots in Scheduling Assistant
        schedulingAssistantAppointmentCreating: (state) => {
            state.newAppointment = {};
        },

        schedulingAssistantAppointmentCreated: (state, action) => {
            state.newAppointment = action.payload;
            if (
                `${action.payload.userId}_${action.payload.date}` in
                state.appointmentsAndSlotsByUserAndDate
            ) {
                state.appointmentsAndSlotsByUserAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ] = [
                    ...state.appointmentsAndSlotsByUserAndDate[
                        `${action.payload.userId}_${action.payload.date}`
                    ],
                    action.payload
                ];
            } else {
                state.appointmentsAndSlotsByUserAndDate = {
                    ...state.appointmentsAndSlotsByUserAndDate,
                    [`${action.payload.userId}_${action.payload.date}`]: [action.payload]
                };
            }
        },

        // Update slots in Scheduling Assistant
        schedulingAssistantAppointmentUpdated: (state, action) => {
            state.usersCurrentAppointment = action.payload;
            if (
                `${action.payload.userId}_${action.payload.date}` in
                state.appointmentsAndSlotsByUserAndDate
            ) {
                state.appointmentsAndSlotsByUserAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ] = state.appointmentsAndSlotsByUserAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ]?.map((el) => (el.id === action.payload.id ? action.payload : el));
            }
        },

        // Delete slots in Scheduling Assistant
        schedulingAssistantAppointmentDeleted: (state, action) => {
            if (
                `${action.payload.userId}_${action.payload.date}` in
                state.appointmentsAndSlotsByUserAndDate
            ) {
                state.appointmentsAndSlotsByUserAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ] = state.appointmentsAndSlotsByUserAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ]?.filter((el) => el.id !== action.payload.id);
            }
        },

        setUsersCurrentAppointment: (state, action) => {
            state.usersCurrentAppointment = action.payload;
        },

        clearNewAppointment: (state) => {
            state.newAppointment = {};
        },

        // User Events
        userEventsByUserAndDateLoading: (state) => {
            state.loadingUserEventsByUserAndDate = true;
        },
        userEventsByUserAndDateLoaded: (state, action) => {
            state.loadingUserEventsByUserAndDate = false;
            const { content, ...rest } = action.payload;
            if (content?.length > 0) {
                content?.forEach((el) => {
                    if (`${el.userId}_${el.date}` in state.userEventsByUserAndDate) {
                        state.userEventsByUserAndDate[`${el.userId}_${el.date}`] = [
                            ...state.userEventsByUserAndDate[`${el.userId}_${el.date}`].filter(
                                (entry) => entry.id !== el.id
                            ),
                            el
                        ];
                    } else {
                        state.userEventsByUserAndDate = {
                            ...state.userEventsByUserAndDate,
                            [`${el.userId}_${el.date}`]: [el]
                        };
                    }
                });
            }
            state.userEventsByUserAndDateMetaData = rest;
        },
        userEventsByUserAndDateLoadingFailure: (state, action) => {
            state.loadingUserEventsByUserAndDate = false;
            state.errorMessage = action.payload;
        },

        userEventsByUserAndDateRangeLoading: (state, action) => {
            state.loadingUserEventsByUserAndDateRange = true;
            state.userDataKeys = action.payload;
        },
        userEventsByUserAndDateRangeLoaded: (state, action) => {
            state.loadingUserEventsByUserAndDateRange = false;
            const { userId, dateFrom, dateTo } = state.userDataKeys;
            const { content, ...rest } = action.payload;
            if (`${userId}_${dateFrom}_${dateTo}` in state.userEventsByUserAndDateRange) {
                state.userEventsByUserAndDateRange[`${userId}_${dateFrom}_${dateTo}`] = rest.first
                    ? content
                    : [
                          ...state.userEventsByUserAndDateRange[`${userId}_${dateFrom}_${dateTo}`],
                          ...content
                      ];
            } else {
                state.userEventsByUserAndDateRange = {
                    ...state.userEventsByUserAndDateRange,
                    [`${userId}_${dateFrom}_${dateTo}`]: content
                };
            }
            state.userEventsByUserAndDateRangeMetaData = rest;
        },
        userEventsByUserAndDateRangeLoadingFailure: (state, action) => {
            state.loadingUserEventsByUserAndDateRange = false;
            state.errorMessage = action.payload;
        },
        clearUserEventsByUserAndDateRange: (state) => {
            state.userEventsByUserAndDateRange = {};
            state.userEventsByUserAndDateRangeMetaData = {};
        },

        userEventCreating: (state, action) => {
            state.userDataKeys = action.payload;
        },
        userEventCreated: (state, action) => {
            const { userId } = state.userDataKeys;
            state.userEventsCreated = {
                ...state.userEventsCreated,
                [userId]:
                    userId in state.userEventsCreated
                        ? [...state.userEventsCreated[userId], ...action.payload]
                        : action.payload
            };
            state.userDataKeys = {};
        },
        clearUserEventsCreated: (state) => {
            state.userEventsCreated = {};
            state.userDataKeys = {};
        },

        userEventUpdating: (state, action) => {
            state.userDataKeys = action.payload;
        },
        userEventUpdated: (state, action) => {
            state.usersCurrentEvent = action.payload;
            const { userId } = state.userDataKeys;
            if (userId in state.userEventsCreated) {
                state.userEventsCreated[userId] = state.userEventsCreated[userId].map((el) =>
                    el.id === action.payload.id ? action.payload : el
                );
            }
            state.userEventsByUserAndDateRange = Object.entries(
                state.userEventsByUserAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.map((el) => (el.id === action.payload.id ? action.payload : el))
                }),
                {}
            );
            state.userDataKeys = {};
        },

        userEventDeleting: (state, action) => {
            state.userDataKeys = action.payload;
        },
        userEventDeleted: (state, action) => {
            const { userId } = state.userDataKeys;
            if (userId in state.userEventsCreated) {
                state.userEventsCreated[userId] = state.userEventsCreated[userId].filter(
                    (el) => el.id !== action.payload.id
                );
            }
            state.userEventsByUserAndDateRange = Object.entries(
                state.userEventsByUserAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.filter((el) => el.id !== action.payload.id)
                }),
                {}
            );
            state.userDataKeys = {};
        },

        setCurrentUserEvent: (state, action) => {
            state.usersCurrentEvent = action.payload;
        },

        setOpenAppointmentSchedulerPanel: (state, action) => {
            state.openAppointmentSchedulerPanel = action.payload;
        },

        successMessageSet: (state, action) => {
            state.successMessage = action.payload;
            state.errorMessage = '';
        },

        errorOccurred: (state, action) => {
            state.errorMessage = action.payload;
        },

        versionLoaded: (state, action) => {
            state.version = action.payload;
        }
    }
});

// AUDIT ---------------------------
export const loadAppointmentAuditData = (section, appointmentId, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getCalendarURL() + `rest/${section}/audit/${appointmentId}/${encodedField}`,
        onStart: appointmentAuditDataLoading.type,
        onSuccess: appointmentAuditDataLoaded.type,
        successMessage: `Appointment entry has been searched`,
        onError: appointmentAuditDataLoadingFailure.type
    });
};

// APPOINTMENTS -------------------------------------
export const searchAppointments = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/appointment/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: appointmentSearchLoading.type,
        onSuccess: appointmentSearchLoaded.type,
        onError: appointmentSearchLoadingFailure.type
    });

export const createAppointment = (appointmentType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment`,
        data,
        method: 'post',
        onStart: appointmentCreating.type,
        onSuccess: appointmentCreated.type,
        successMessage: `Appointment ${appointmentType} has been created`,
        onError: errorOccurred.type
    });

// UserCalendar
export const createUserCalendarAppointment = (appointmentType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment`,
        data,
        method: 'post',
        onStartData: { userId: data.userId },
        onStart: userCalendarAppointmentCreating.type,
        onSuccess: userCalendarAppointmentCreated.type,
        successMessage: `Appointment ${appointmentType} has been created`,
        onError: errorOccurred.type
    });

export const updateUserCalendarAppointment = (userId, appointmentType, data, hash = '01') =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment/${data.id}`,
        data,
        method: 'put',
        onStartData: { id: data.id, userId },
        onStart: userCalendarAppointmentUpdating.type,
        onSuccess: userCalendarAppointmentUpdated.type,
        successMessage: `Appointment ${appointmentType} has been updated (${hash})`,
        onError: errorOccurred.type
    });

export const deleteUserCalendarAppointment = (userId, name, id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment/${id}`,
        data: { userId },
        method: 'delete',
        onStartData: { userId },
        onStart: userCalendarAppointmentDeleting.type,
        onSuccess: userCalendarAppointmentDeleted.type,
        successMessage: `Appointment ${name} has been deleted`,
        onError: errorOccurred.type
    });

// Participant Calendar
export const createParticipantCalendarAppointment = (appointmentType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment`,
        data,
        method: 'post',
        onStartData: { participantId: data.participantId },
        onStart: participantCalendarAppointmentCreating.type,
        onSuccess: participantCalendarAppointmentCreated.type,
        successMessage: `Appointment ${appointmentType} has been created`,
        onError: errorOccurred.type
    });

export const updateParticipantCalendarAppointment = (
    participantId,
    appointmentType,
    data,
    hash = '01'
) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment/${data.id}`,
        data,
        method: 'put',
        onStartData: { id: data.id, participantId },
        onStart: participantCalendarAppointmentUpdating.type,
        onSuccess: participantCalendarAppointmentUpdated.type,
        successMessage: `Appointment ${appointmentType} has been updated (${hash})`,
        onError: errorOccurred.type
    });

export const deleteParticipantCalendarAppointment = (participantId, userId, name, id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment/${id}`,
        data: { userId },
        method: 'delete',
        onStartData: { participantId },
        onStart: participantCalendarAppointmentDeleting.type,
        onSuccess: participantCalendarAppointmentDeleted.type,
        successMessage: `Appointment ${name} has been deleted`,
        onError: errorOccurred.type
    });

// Scheduling Assistant
export const createSchedulingAssistantAppointment = (appointmentType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment`,
        data,
        method: 'post',
        onStart: schedulingAssistantAppointmentCreating.type,
        onSuccess: schedulingAssistantAppointmentCreated.type,
        successMessage: `Appointment ${appointmentType} has been created`,
        onError: errorOccurred.type
    });

// SLOTS ---------------------------------------------
export const loadAppointmentSlot = (id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${id}`,
        onSuccess: appointmentLoaded.type,
        onError: errorOccurred.type
    });

// User Calendar
export const createUserCalendarAppointmentSlot = (slotType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot`,
        data,
        method: 'post',
        onStartData: { userId: data.userId },
        onStart: userCalendarAppointmentCreating.type,
        onSuccess: userCalendarAppointmentCreated.type,
        successMessage: `Appointment slot ${slotType} has been created`,
        onError: errorOccurred.type
    });

export const updateUserCalendarAppointmentSlot = (userId, appointmentType, data, hash = '01') =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${data.id}`,
        data,
        method: 'put',
        onStartData: { id: data.id, userId },
        onStart: userCalendarAppointmentUpdating.type,
        onSuccess: userCalendarAppointmentUpdated.type,
        successMessage: `Appointment slot ${appointmentType} has been updated (${hash})`,
        onError: errorOccurred.type
    });

export const deleteUserCalendarAppointmentSlot = (userId, name, id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${id}`,
        data: { userId },
        method: 'delete',
        onStartData: { userId },
        onStart: userCalendarAppointmentDeleting.type,
        onSuccess: userCalendarAppointmentDeleted.type,
        successMessage: `Appointment slot ${name} has been deleted`,
        onError: errorOccurred.type
    });

// Participant Calendar
export const updateParticipantCalendarAppointmentSlot = (
    participantId,
    appointmentType,
    data,
    hash = '01'
) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${data.id}`,
        data,
        method: 'put',
        onStartData: { id: data.id, participantId },
        onStart: participantCalendarAppointmentUpdating.type,
        onSuccess: participantCalendarAppointmentUpdated.type,
        successMessage: `Appointment slot ${appointmentType} has been updated (${hash})`,
        onError: errorOccurred.type
    });

export const deleteParticipantCalendarAppointmentSlot = (participantId, userId, name, id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${id}`,
        data: { userId },
        method: 'delete',
        onStartData: { participantId },
        onStart: participantCalendarAppointmentDeleting.type,
        onSuccess: participantCalendarAppointmentDeleted.type,
        successMessage: `Appointment slot ${name} has been deleted`,
        onError: errorOccurred.type
    });

// Scheduling Assistant
export const createSchedulingAssistantAppointmentSlot = (slotType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot`,
        data,
        method: 'post',
        onStart: schedulingAssistantAppointmentCreating.type,
        onSuccess: schedulingAssistantAppointmentCreated.type,
        successMessage: `Appointment slot ${slotType} has been created`,
        onError: errorOccurred.type
    });

export const updateSchedulingAssistantAppointmentSlot = (
    userId,
    appointmentType,
    data,
    hash = '01'
) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${data.id}`,
        data,
        method: 'put',
        onStartData: { id: data.id, userId },
        onSuccess: schedulingAssistantAppointmentUpdated.type,
        successMessage: `Appointment slot ${appointmentType} has been updated (${hash})`,
        onError: errorOccurred.type
    });

export const deleteSchedulingAssistantAppointmentSlot = (userId, name, id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${id}`,
        method: 'delete',
        data: { userId },
        onSuccess: schedulingAssistantAppointmentDeleted.type,
        successMessage: `Appointment slot ${name} has been deleted`,
        onError: errorOccurred.type
    });

// appointment slots table
export const searchAppointmentSlots = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/appointment-slot/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: appointmentSlotsSearchLoading.type,
        onSuccess: appointmentSlotsSearchLoaded.type,
        onError: appointmentSlotsSearchLoadingFailure.type
    });

export const updateAppointmentSlotSearch = (appointmentType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/appointment-slot/${data.id}`,
        data,
        method: 'put',
        successMessage: `Appointment slot ${appointmentType} has been updated`,
        onError: errorOccurred.type
    });

// BOTH APPOINTMENT AND SLOTS (BASE APPOINTMENTS)---------------------------------------------------------
export const loadBaseAppointment = (id) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/base-appointment/${id}`,
        onStart: baseAppointmentLoading.type,
        onSuccess: baseAppointmentLoaded.type,
        successMessage: `Base appointment has been loaded`,
        onError: baseAppointmentLoadingFailure.type
    });

export const searchAppointmentsAndSlots = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/base-appointment/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: appointmentsAndSlotsSearchLoading.type,
        onSuccess: appointmentsAndSlotsSearchLoaded.type,
        successMessage: `Appointments and slots loaded`,
        onError: appointmentsAndSlotsSearchLoadingFailure.type
    });

// PT and User Calendars
export const searchAppointmentsAndSlotsByUserAndDateRange = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/base-appointment/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStartData: { ...data, userId: data.userIds[0] },
        onStart: appointmentsAndSlotsByUserAndDateRangeLoading.type,
        onSuccess: appointmentsAndSlotsByUserAndDateRangeLoaded.type,
        successMessage: `Appointments and slots loaded`,
        onError: appointmentsAndSlotsByUserAndDateRangeLoadingFailure.type
    });

// PT Calendar
export const searchAppointmentsAndSlotsByParticipantAndDateRange = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/base-appointment/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStartData: { ...data, participantId: data.participantIds[0] },
        onStart: appointmentsAndSlotsByParticipantAndDateRangeLoading.type,
        onSuccess: appointmentsAndSlotsByParticipantAndDateRangeLoaded.type,
        successMessage: `Appointments and slots loaded`,
        onError: appointmentsAndSlotsByParticipantAndDateRangeLoadingFailure.type
    });

// Scheduling Assistant
export const searchUsersAppointmentsAndSlotsByDate = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'userId',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/base-appointment/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: usersAppointmentsAndSlotsByDateLoading.type,
        onSuccess: usersAppointmentsAndSlotsByDateLoaded.type,
        successMessage: `Appointments and slots loaded`,
        onError: usersAppointmentsAndSlotsByDateLoadingFailure.type
    });

// ATTENDANCE
export const searchAppointmentAttendanceDetails = (data, page = 0, size = DEFAULT_PAGE_LOAD_SIZE) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/attendance/search?page=${page}&size=${size}`,
        method: 'post',
        data,
        onStart: appointmentAttendanceDetailsSearchLoading.type,
        onSuccess: appointmentAttendanceDetailsSearchLoaded.type,
        successMessage: `Appointment attendance details loaded`,
        onError: appointmentAttendanceDetailsSearchLoadingFailure.type
    });

export const loadAppointmentAttendanceDetails = (participantId) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/attendance/participant/${participantId}`,
        onStart: appointmentAttendanceDetailsLoading.type,
        onSuccess: appointmentAttendanceDetailsLoaded.type,
        successMessage: `Appointment attendance details loaded`,
        onError: appointmentAttendanceDetailsLoadingFailure.type
    });

export const loadMostRecentAppointmentAttendanceDetails = (participantId) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/attendance/participant/${participantId}/recent`,
        onSuccess: mostRecentAppointmentAttendanceDetailsLoaded.type,
        successMessage: `Last appointment loaded`,
        onError: errorOccurred.type
    });

export const updateAppointmentAttendanceDetail = (data, id, msg = `Appointment updated`) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/attendance/${id}`,
        method: 'put',
        data,
        onSuccess: appointmentAttendanceDetailUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const searchParticipantsLastAndNextAppointments = (participantIds) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/attendance/last-next`,
        method: 'post',
        data: { participantIds },
        onSuccess: participantsLastAndNextAppointmentsLoaded.type,
        successMessage: `Participants last and next appointments loaded`,
        onError: errorOccurred.type
    });

// USER EVENTS
export const searchUserEventsByUserAndDate = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'userId',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/user-event/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        data,
        method: 'post',
        onStart: userEventsByUserAndDateLoading.type,
        onSuccess: userEventsByUserAndDateLoaded.type,
        successMessage: `User events loaded`,
        onError: userEventsByUserAndDateLoadingFailure.type
    });

export const searchUserEventsByUserAndDateRange = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCalendarURL() +
            `rest/user-event/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        data,
        method: 'post',
        onStartData: { ...data, userId: data.userIds[0] },
        onStart: userEventsByUserAndDateRangeLoading.type,
        onSuccess: userEventsByUserAndDateRangeLoaded.type,
        successMessage: `User events loaded`,
        onError: userEventsByUserAndDateRangeLoadingFailure.type
    });

export const createUserEvent = (userEventType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/user-event`,
        data,
        method: 'post',
        onStartData: { userId: data.userId },
        onStart: userEventCreating.type,
        onSuccess: userEventCreated.type,
        successMessage: `User event ${userEventType} has been created`,
        onError: errorOccurred.type
    });

export const updateUserEvent = (id, userEventType, data) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/user-event/${id}`,
        data,
        method: 'put',
        onStartData: { userId: data.userId },
        onStart: userEventUpdating.type,
        onSuccess: userEventUpdated.type,
        successMessage: `User event ${userEventType} has been updated`,
        onError: errorOccurred.type
    });

export const deleteUserEvent = (id, userId, userEventType) =>
    apiCallBegan({
        url: getCalendarURL() + `rest/user-event/${id}`,
        method: 'delete',
        onStartData: { userId },
        onStart: userEventDeleting.type,
        onSuccess: userEventDeleted.type,
        successMessage: `User event ${userEventType} has been deleted`,
        onError: errorOccurred.type
    });

// VERSION
export const loadCalendarServiceVersion = () =>
    apiVersion({
        url: getCalendarURL() + 'rest/version',
        method: 'get',
        onSuccess: versionLoaded.type,
        onError: errorOccurred.type
    });

export const {
    clearAppointmentAttendanceDetails,
    clearAppointmentAuditData,
    clearAppointmentSlotsSearch,
    clearAppointmentSearch,
    clearAppointmentsAndSlotsSearch,
    clearAppointmentsAndSlotsByUserAndDateRange,
    clearAppointmentsCreated,
    clearNewAppointment,
    clearAppointmentsAndSlotsByParticipantAndDateRange,
    clearAppointmentsAndSlotsByParticipantAndDateRangeMetaData,
    clearParticipantsLastAndNextAppointments,

    clearUserEventsCreated,
    clearUserEventsByUserAndDateRange,

    setParticipantsCurrentAppointment,
    setUsersCurrentAppointment,
    setCurrentUserEvent,
    setOpenAppointmentSchedulerPanel
} = slice.actions;

const {
    appointmentAuditDataLoading,
    appointmentAuditDataLoaded,
    appointmentAuditDataLoadingFailure,

    appointmentAttendanceDetailsLoading,
    appointmentAttendanceDetailsLoaded,
    appointmentAttendanceDetailsLoadingFailure,
    appointmentAttendanceDetailsSearchLoading,
    appointmentAttendanceDetailsSearchLoaded,
    appointmentAttendanceDetailsSearchLoadingFailure,
    appointmentAttendanceDetailUpdated,
    mostRecentAppointmentAttendanceDetailsLoaded,

    appointmentSearchLoading,
    appointmentSearchLoaded,
    appointmentSearchLoadingFailure,
    appointmentSlotsSearchLoading,
    appointmentSlotsSearchLoaded,
    appointmentSlotsSearchLoadingFailure,
    appointmentsAndSlotsSearchLoading,
    appointmentsAndSlotsSearchLoaded,
    appointmentsAndSlotsSearchLoadingFailure,

    appointmentsAndSlotsByParticipantAndDateRangeLoading,
    appointmentsAndSlotsByParticipantAndDateRangeLoadingFailure,
    appointmentsAndSlotsByParticipantAndDateRangeLoaded,
    participantsLastAndNextAppointmentsLoaded,

    appointmentCreating,
    appointmentCreated,
    appointmentLoaded,
    baseAppointmentLoading,
    baseAppointmentLoaded,
    baseAppointmentLoadingFailure,
    usersAppointmentsAndSlotsByDateLoading,
    usersAppointmentsAndSlotsByDateLoaded,
    usersAppointmentsAndSlotsByDateLoadingFailure,
    appointmentsAndSlotsByUserAndDateRangeLoading,
    appointmentsAndSlotsByUserAndDateRangeLoadingFailure,
    appointmentsAndSlotsByUserAndDateRangeLoaded,

    participantCalendarAppointmentCreating,
    participantCalendarAppointmentCreated,
    participantCalendarAppointmentDeleting,
    participantCalendarAppointmentDeleted,
    participantCalendarAppointmentUpdating,
    participantCalendarAppointmentUpdated,

    userCalendarAppointmentCreating,
    userCalendarAppointmentCreated,
    userCalendarAppointmentDeleting,
    userCalendarAppointmentDeleted,
    userCalendarAppointmentUpdating,
    userCalendarAppointmentUpdated,

    schedulingAssistantAppointmentCreating,
    schedulingAssistantAppointmentCreated,
    schedulingAssistantAppointmentDeleted,
    schedulingAssistantAppointmentUpdated,

    userEventCreating,
    userEventCreated,
    userEventUpdating,
    userEventUpdated,
    userEventDeleting,
    userEventDeleted,
    userEventsByUserAndDateLoading,
    userEventsByUserAndDateLoaded,
    userEventsByUserAndDateLoadingFailure,
    userEventsByUserAndDateRangeLoading,
    userEventsByUserAndDateRangeLoaded,
    userEventsByUserAndDateRangeLoadingFailure,

    errorOccurred,
    versionLoaded
} = slice.actions;

export default slice.reducer;
